import { useEffect, useMemo, useState } from 'react'
import { useIsPortrait } from '../../../util/breakpoints'
import styled from '@emotion/styled'
import { useInView } from 'react-intersection-observer'
import { VideoPlayer } from '../../atoms/video/VideoPlayer'
import { getFirstFrameUrl } from '../../../util/path'

export interface Video {
  videoId: string
  videoLandscape: string
  videoPortrait: string
  scrollPercentage: number
  alt: string
  playing: boolean
  type: 'loop' | 'play_once' | 'play_once_with_controls'
  lang: string
}

export default function Video(props: Video) {
  const [videoBuffered, setVideoBuffered] = useState(false)
  const isPortrait = useIsPortrait()

  const { ref, inView: videoMostlyInView } = useInView({
    threshold: 0.85,
  })

  const posterUrl = isPortrait
    ? getFirstFrameUrl(props.videoPortrait)
    : getFirstFrameUrl(props.videoLandscape)

  const MUTE_STATE_KEY = 'scrollstory_scrollOverMedia_video_muted'

  // Only on first render: Remove session storage item. Might be still there if window was refreshed
  useEffect(() => {
    sessionStorage.removeItem(MUTE_STATE_KEY)
  }, [])

  const onMuteChange = (muted: boolean) => {
    sessionStorage.setItem(MUTE_STATE_KEY, muted.toString())
  }

  const getSavedMuteState = (): boolean => {
    const savedMuteState = sessionStorage.getItem(MUTE_STATE_KEY)
    if (savedMuteState) {
      return savedMuteState === 'true'
    } else {
      return true
    }
  }

  const coverIsNotVisible = props.scrollPercentage > 0.05

  const memoizedPlayer = useMemo(() => {
    const videoUrl = isPortrait ? props.videoPortrait : props.videoLandscape

    let muted = true
    if (props.type === 'play_once_with_controls') {
      muted = getSavedMuteState()
    }

    const videoIsPlaying = () => {
      if (props.playing && videoMostlyInView) {
        if (props.type === 'play_once_with_controls') {
          return coverIsNotVisible
        } else {
          return true
        }
      }

      return false
    }

    return (
      <VideoPlayer
        videoUrl={videoUrl}
        onBufferEnd={() => setVideoBuffered(true)}
        playing={videoIsPlaying()}
        controls={props.type === 'play_once_with_controls' ? 'custom' : 'none'}
        loop={props.type === 'loop'}
        subtitleLang={props.lang}
        muted={muted}
        fullscreen={true}
        alt={props.alt}
        aria-label={props.alt}
        onMuteChange={onMuteChange}
      />
    )
  }, [
    videoMostlyInView,
    props.videoPortrait,
    props.videoLandscape,
    props.playing,
    props.type,
    coverIsNotVisible,
    isPortrait,
    props.alt,
    props.lang,
  ])

  return (
    <>
      <VideoContainer ref={ref}>
        <Poster posterIsVisible={!videoBuffered} posterUrl={posterUrl} />
        {(props.playing || videoBuffered) && memoizedPlayer}
      </VideoContainer>
    </>
  )
}

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
`

interface PosterProps {
  posterIsVisible: boolean
  posterUrl: string
}
const Poster = styled.div<PosterProps>`
  position: absolute;
  opacity: ${(props) => (props.posterIsVisible ? '1' : '0')};
  transition: opacity 0.5s ease-in-out;
  pointer-events: none;
  z-index: 3;
  height: 100vh;
  width: 100%;
  background-image: url(${(props) => props.posterUrl});
  background-position: center;
  background-size: cover;
`
